import React, {useMemo} from "react";
import ReactEcharts from "echarts-for-react";
import {IDailyData} from "../models/roi-widget.state";

interface IROIBarChartProps {
    currentData: IDailyData[];
    previousData?: IDailyData[];
    metric: "count" | "timeSaved" | "costSaved";
    currency?: string;
    showComparison?: boolean;
    height?: number;
}

const ROIBarChart: React.FC<IROIBarChartProps> = ({
    currentData,
    previousData,
    metric,
    currency = "USD",
    showComparison = false,
    height = 200,
}) => {
    const chartOptions = useMemo(() => {
        // Prepare categories (dates)
        const categories = currentData.map((item) => {
            const date = new Date(item.day);
            return date.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
            });
        });

        // Prepare data for current period
        const currentValues = currentData.map((item) => {
            switch (metric) {
                case "timeSaved":
                    return item.timeSaved;
                case "costSaved":
                    return item.costSaved;
                default:
                    return item.count;
            }
        });

        // Prepare data for previous period if comparison is enabled
        const previousValues =
            showComparison && previousData
                ? previousData.map((item) => {
                      switch (metric) {
                          case "timeSaved":
                              return item.timeSaved;
                          case "costSaved":
                              return item.costSaved;
                          default:
                              return item.count;
                      }
                  })
                : [];

        // Get metric label and unit
        const getMetricInfo = () => {
            switch (metric) {
                case "timeSaved":
                    return {label: "Time Saved", unit: " hrs"};
                case "costSaved":
                    return {label: "Cost Saved", unit: currency === "USD" ? " $" : ` ${currency}`};
                default:
                    return {label: "Activities", unit: ""};
            }
        };

        const metricInfo = getMetricInfo();

        const series = [
            {
                name: "Current Period",
                type: "bar",
                data: currentValues,
                itemStyle: {
                    color: "#29abe2",
                    borderRadius: [4, 4, 0, 0],
                },
                label: {
                    show: false,
                    position: "top",
                    formatter: (params: any) => {
                        const value =
                            metric === "costSaved"
                                ? `${metricInfo.unit}${params.value.toLocaleString()}`
                                : `${params.value.toLocaleString()}${metricInfo.unit}`;
                        return value;
                    },
                },
            },
        ];

        // Add previous period series if comparison is enabled
        if (showComparison && previousValues.length > 0) {
            series.push({
                name: "Previous Period",
                type: "bar",
                data: previousValues,
                itemStyle: {
                    color: "#E64E7C",
                    borderRadius: [4, 4, 0, 0],
                },
                label: {
                    show: false,
                    position: "top",
                    formatter: (params: any) => {
                        const value =
                            metric === "costSaved"
                                ? `${metricInfo.unit}${params.value.toLocaleString()}`
                                : `${params.value.toLocaleString()}${metricInfo.unit}`;
                        return value;
                    },
                },
            });
        }

        return {
            tooltip: {
                trigger: "axis",
                axisPointer: {
                    type: "shadow",
                },
                formatter: (params: any) => {
                    let tooltip = `<div style="margin-bottom: 5px;">${params[0].axisValue}</div>`;
                    params.forEach((param: any) => {
                        const value =
                            metric === "costSaved"
                                ? `${metricInfo.unit}${param.value.toLocaleString()}`
                                : `${param.value.toLocaleString()}${metricInfo.unit}`;
                        tooltip += `
                            <div style="display: flex; align-items: center; margin-bottom: 2px;">
                                <span style="display: inline-block; width: 10px; height: 10px; background-color: ${param.color}; margin-right: 8px;"></span>
                                <span style="margin-right: 8px;">${param.seriesName}:</span>
                                <span style="font-weight: bold;">${value}</span>
                            </div>
                        `;
                    });
                    return tooltip;
                },
            },
            legend: {
                data: showComparison ? ["Current Period", "Previous Period"] : ["Current Period"],
                top: 10,
                textStyle: {
                    color: "#666",
                },
            },
            grid: {
                left: "10%",
                right: "10%",
                bottom: "15%",
                top: showComparison ? "20%" : "15%",
                containLabel: true,
            },
            xAxis: {
                type: "category",
                data: categories,
                axisLine: {
                    lineStyle: {
                        color: "#e0e0e0",
                    },
                },
                axisLabel: {
                    color: "#666",
                    fontSize: 12,
                    rotate: 45,
                    interval: 0,
                },
                splitLine: {
                    show: false,
                },
            },
            yAxis: {
                type: "value",
                axisLine: {
                    lineStyle: {
                        color: "#e0e0e0",
                    },
                },
                axisLabel: {
                    color: "#666",
                    fontSize: 12,
                    formatter: (value: number) => {
                        if (metric === "costSaved") {
                            return `${metricInfo.unit}${value.toLocaleString()}`;
                        }
                        return `${value.toLocaleString()}${metricInfo.unit}`;
                    },
                },
                splitLine: {
                    lineStyle: {
                        color: "#f5f5f5",
                        type: "dashed",
                    } as any,
                },
            },
            series,
        };
    }, [currentData, previousData, metric, currency, showComparison]);

    return (
        <div className="roi-widget__chart">
            <ReactEcharts
                option={chartOptions}
                style={{height: `${height}px`, width: "100%"}}
                opts={{renderer: "canvas"}}
            />
        </div>
    );
};

export default ROIBarChart;
